/**
 * Mobile drawer:
 * - Top-level accordion (Shop open by default; other roots stay visible)
 * - Nested levels keep Dawn slide-over, opened immediately (no 100ms delay)
 * - Non-blocking Account/Track feedback; drawer closes before navigation so
 *   browser Back never restores its backdrop
 */
(() => {
  const TOP_LEVEL = '.menu-drawer__navigation > .menu-drawer__menu > li > details';

  function topLevelDetails(drawer) {
    return Array.from(drawer.querySelectorAll(TOP_LEVEL));
  }

  function closeTopLevelExcept(drawer, keep) {
    topLevelDetails(drawer).forEach((details) => {
      if (details === keep) return;
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      details.querySelectorAll('.submenu-open').forEach((el) => el.classList.remove('submenu-open'));
      details.querySelectorAll('details[open]').forEach((sub) => {
        sub.removeAttribute('open');
        sub.classList.remove('menu-opening');
      });
    });
  }

  function openShopDefault(drawer) {
    const shop = drawer.querySelector('#HeaderDrawer-shop')?.closest('details');
    if (!shop) return;
    closeTopLevelExcept(drawer, shop);
    shop.setAttribute('open', '');
    shop.classList.remove('menu-opening');
    shop.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
  }

  function openNestedNow(details) {
    const summary = details.querySelector(':scope > summary');
    const parentMenu = details.closest('.has-submenu');
    details.setAttribute('open', '');
    details.classList.add('menu-opening');
    summary?.setAttribute('aria-expanded', 'true');
    parentMenu?.classList.add('submenu-open');
  }

  function onSummaryCapture(event) {
    const summary = event.currentTarget;
    const details = summary.parentNode;
    const drawer = summary.closest('header-drawer');
    if (!drawer || !details) return;

    if (details.classList.contains('menu-drawer-container')) return;

    const isTopLevel = details.matches(TOP_LEVEL);

    if (isTopLevel) {
      // Accordion — do not let Dawn hide the root list with submenu-open
      event.preventDefault();
      event.stopImmediatePropagation();

      const isOpen = details.hasAttribute('open');
      if (isOpen) {
        details.removeAttribute('open');
        summary.setAttribute('aria-expanded', 'false');
      } else {
        closeTopLevelExcept(drawer, details);
        details.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    // Nested: speed up Dawn's delayed menu-opening
    if (!details.hasAttribute('open')) {
      requestAnimationFrame(() => {
        if (details.hasAttribute('open')) openNestedNow(details);
      });
    }
  }

  function resetAccountLoadingUi() {
    document.querySelectorAll('.grail-account-nav-status').forEach((el) => el.remove());
    document.querySelectorAll('[data-grail-account-link].is-loading').forEach((link) => {
      link.classList.remove('is-loading');
      const label = link.querySelector('.grail-drawer-quick__label');
      if (!label) return;
      label.textContent =
        link.getAttribute('data-grail-label') === 'track' ? 'Track Order' : 'My Account';
    });
  }

  function closeDrawer() {
    document.querySelectorAll('header-drawer details.menu-drawer-container[open]').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      details.querySelectorAll('details').forEach((sub) => {
        sub.removeAttribute('open');
        sub.classList.remove('menu-opening');
        sub.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      });
      details.querySelectorAll('.submenu-open').forEach((el) => el.classList.remove('submenu-open'));
    });
    document.body.classList.remove('overflow-hidden-tablet', 'overflow-hidden-desktop', 'grail-nav-open');
    document.querySelector('.section-header.menu-open')?.classList.remove('menu-open');
  }

  function showAccountStatus() {
    const status = document.createElement('div');
    status.className = 'grail-account-nav-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.innerHTML =
      '<span class="grail-account-nav-status__spinner" aria-hidden="true"></span>' +
      '<span>Opening secure account…</span>';
    document.body.appendChild(status);
  }

  function initAccountLinkFeedback() {
    document.querySelectorAll('[data-grail-account-link]').forEach((link) => {
      if (link.dataset.grailAccountBound === 'true') return;
      link.dataset.grailAccountBound = 'true';

      link.addEventListener('click', () => {
        // Do not prevent navigation or rewrite the trusted Shopify account URL.
        // Closing first ensures the bfcache snapshot cannot contain the backdrop.
        closeDrawer();
        resetAccountLoadingUi();

        const label = link.querySelector('.grail-drawer-quick__label');
        link.classList.add('is-loading');
        if (label) label.textContent = link.getAttribute('data-grail-loading-label') || 'Opening…';
        showAccountStatus();
      });
    });
  }

  function initDrawer(drawer) {
    if (!drawer || drawer.dataset.grailNavInit === 'true') return;
    drawer.dataset.grailNavInit = 'true';

    drawer.querySelectorAll('.menu-drawer summary').forEach((summary) => {
      summary.addEventListener('click', onSummaryCapture, true);
    });

    const mainDetails = drawer.querySelector('details.menu-drawer-container');
    if (mainDetails) {
      mainDetails.addEventListener('toggle', () => {
        if (mainDetails.open) openShopDefault(drawer);
      });
    }
  }

  function initAll() {
    document.querySelectorAll('header-drawer').forEach(initDrawer);
    initAccountLinkFeedback();
    // Clear before bfcache freeze and again after Back/forward restoration.
    window.addEventListener('pagehide', resetAccountLoadingUi);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') resetAccountLoadingUi();
    });
    window.addEventListener('pageshow', () => {
      resetAccountLoadingUi();
      closeDrawer();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
